<template>
  <v-dialog
    :model-value="modelValue"
    max-width="640"
    persistent
    @update:model-value="onDialogUpdate"
  >
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span>{{ mode === 'review' ? 'Select Area' : 'Take Photo' }}</span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="close" />
      </v-card-title>

      <v-card-text>
        <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <template v-else>
          <v-select
            v-if="deviceOptions.length > 1 && mode === 'live'"
            v-model="selectedDeviceId"
            :items="deviceOptions"
            item-title="title"
            item-value="value"
            label="Camera"
            density="comfortable"
            variant="outlined"
            hide-details
            prepend-inner-icon="mdi-camera-switch"
            class="mb-3"
            @update:model-value="onDeviceChange"
          />

          <div v-show="mode === 'live'" class="camera-preview-wrapper mb-3">
            <video ref="videoRef" autoplay playsinline muted class="camera-preview" />
            <div v-if="isStarting" class="camera-preview-overlay">
              <v-progress-circular indeterminate color="white" />
            </div>
          </div>

          <div v-if="mode === 'review'" class="mb-2">
            <div
              ref="cropContainerRef"
              class="crop-container"
              :style="cropContainerStyle"
              @pointerdown="onBackgroundPointerDown"
            >
              <img
                :src="reviewImageUrl"
                class="crop-image"
                draggable="false"
                alt="Captured photo preview"
              />
              <div class="crop-rect" :style="cropRectStyle" @pointerdown.stop="onRectPointerDown">
                <div
                  class="crop-handle crop-handle-nw"
                  @pointerdown.stop="onHandlePointerDown('nw', $event)"
                />
                <div
                  class="crop-handle crop-handle-ne"
                  @pointerdown.stop="onHandlePointerDown('ne', $event)"
                />
                <div
                  class="crop-handle crop-handle-sw"
                  @pointerdown.stop="onHandlePointerDown('sw', $event)"
                />
                <div
                  class="crop-handle crop-handle-se"
                  @pointerdown.stop="onHandlePointerDown('se', $event)"
                />
              </div>
            </div>
            <div class="text-caption text-grey mt-1">
              Drag the box to move it, drag a corner to resize, or drag anywhere on the photo to draw
              a new selection.
            </div>
          </div>

          <div v-if="capturedShots.length" class="d-flex flex-wrap" style="gap: 8px">
            <div v-for="shot in capturedShots" :key="shot.id" class="captured-thumb">
              <v-img :src="shot.previewUrl" width="72" height="72" cover class="rounded-lg border" />
              <v-btn
                icon="mdi-close"
                size="x-small"
                variant="flat"
                color="white"
                class="captured-thumb-remove"
                :aria-label="'Remove captured photo'"
                @click="removeShot(shot.id)"
              />
            </div>
          </div>
        </template>
      </v-card-text>

      <canvas ref="canvasRef" class="d-none" />

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" data-testid="camera-cancel-button" @click="close">Cancel</v-btn>

        <v-btn v-if="errorMessage" variant="outlined" color="primary" @click="retryStream">
          <v-icon start icon="mdi-refresh" />
          Retry
        </v-btn>

        <template v-else-if="mode === 'review'">
          <v-btn variant="outlined" color="primary" data-testid="camera-retake-button" @click="retake">
            <v-icon start icon="mdi-camera-retake" />
            Retake
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            data-testid="camera-add-photo-button"
            :loading="isAddingPhoto"
            :disabled="isAddingPhoto"
            @click="applyCropAndAdd"
          >
            <v-icon start icon="mdi-crop" />
            Add Photo
          </v-btn>
        </template>

        <template v-else>
          <v-btn
            color="primary"
            variant="outlined"
            data-testid="camera-capture-button"
            :disabled="isStarting"
            @click="capturePhoto"
          >
            <v-icon start icon="mdi-camera" />
            Capture
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="capturedShots.length === 0"
            @click="finish"
          >
            Use {{ capturedShots.length || '' }} Photo{{ capturedShots.length === 1 ? '' : 's' }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'capture', files: File[]): void
}>()

interface CapturedShot {
  id: string
  file: File
  previewUrl: string
}

interface NormalizedRect {
  x: number
  y: number
  width: number
  height: number
}

type DragMode = 'move' | 'draw' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se' | null
type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se'

const FULL_RECT: NormalizedRect = { x: 0, y: 0, width: 1, height: 1 }
const MIN_RECT_SIZE = 0.05
// Default selection is a centered square (equal width/height in pixels) with
// a bit of padding around it, rather than corner-to-corner on the full frame.
const DEFAULT_RECT_PADDING_RATIO = 0.1

const computeDefaultRect = (width: number, height: number): NormalizedRect => {
  if (!width || !height) return { ...FULL_RECT }
  const shortSide = Math.min(width, height)
  const squareSize = shortSide * (1 - DEFAULT_RECT_PADDING_RATIO * 2)
  const offsetX = (width - squareSize) / 2
  const offsetY = (height - squareSize) / 2
  return {
    x: offsetX / width,
    y: offsetY / height,
    width: squareSize / width,
    height: squareSize / height,
  }
}

const videoRef = ref<HTMLVideoElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const devices = ref<MediaDeviceInfo[]>([])
const selectedDeviceId = ref<string | undefined>(undefined)
const activeStream = ref<MediaStream | null>(null)
const errorMessage = ref('')
const isStarting = ref(false)
const capturedShots = ref<CapturedShot[]>([])

// Review / crop-selection state
const mode = ref<'live' | 'review'>('live')
const reviewImageUrl = ref('')
const reviewImageWidth = ref(0)
const reviewImageHeight = ref(0)
const cropRect = ref<NormalizedRect>({ ...FULL_RECT })
const cropContainerRef = ref<HTMLDivElement | null>(null)
const dragMode = ref<DragMode>(null)
const dragStartPointer = ref<{ x: number; y: number } | null>(null)
const dragStartRect = ref<NormalizedRect | null>(null)

const deviceOptions = computed(() =>
  devices.value.map((device, index) => ({
    title: device.label || `Camera ${index + 1}`,
    value: device.deviceId,
  })),
)

const cropContainerStyle = computed(() => ({
  aspectRatio:
    reviewImageWidth.value && reviewImageHeight.value
      ? `${reviewImageWidth.value} / ${reviewImageHeight.value}`
      : '4 / 3',
}))

const cropRectStyle = computed(() => ({
  left: `${cropRect.value.x * 100}%`,
  top: `${cropRect.value.y * 100}%`,
  width: `${cropRect.value.width * 100}%`,
  height: `${cropRect.value.height * 100}%`,
}))

const generateId = () =>
  (crypto as { randomUUID?: () => string })?.randomUUID?.() || `${Date.now()}-${Math.random()}`

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const stopActiveStream = () => {
  activeStream.value?.getTracks().forEach((track) => track.stop())
  activeStream.value = null
}

// getUserMedia() is asynchronous, so a request can still be in flight when the
// user closes the dialog or switches cameras again. Without guarding against
// that, a stale request could resolve after the fact and either leave the
// camera running behind a closed dialog, or clobber a newer stream with an
// older one. Every call to startStream() gets its own id; when a request
// resolves we check it's still both the latest request and the dialog is
// still open before adopting the stream — otherwise we release it immediately.
let activeStreamRequestId = 0

// Belt-and-braces: bump the request id directly (rather than relying solely on
// `props.modelValue` having propagated yet) so any in-flight getUserMedia()
// call is marked stale the instant we close/unmount, regardless of prop timing.
const invalidatePendingStreamRequests = () => {
  activeStreamRequestId += 1
}

// canvas.toBlob() (used to turn a captured frame, or a crop of it, into a
// JPEG) is also asynchronous. If the user hits Retake/Cancel — or starts a
// new capture — before a pending conversion finishes, that conversion must
// not be allowed to add its (now-stale) result into the dialog's state.
// Every capture/crop operation gets its own id, checked the same way as
// camera stream requests above.
let photoOperationId = 0
const isAddingPhoto = ref(false)

const invalidatePendingPhotoOperations = () => {
  photoOperationId += 1
  isAddingPhoto.value = false
}

const describeError = (err: unknown): string => {
  const domError = err as DOMException
  switch (domError?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Camera access was denied. Please allow camera permissions in your browser and try again.'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No camera was found on this device.'
    case 'NotReadableError':
    case 'TrackStartError':
      return 'The camera is already in use by another application.'
    case 'OverconstrainedError':
      return 'The selected camera is not available. Please choose a different camera.'
    default:
      return (err as Error)?.message || 'Unable to access the camera.'
  }
}

const startStream = async (deviceId?: string) => {
  errorMessage.value = ''
  isStarting.value = true
  stopActiveStream()

  // Snapshot this request's id. If a newer request starts (device switch,
  // retry, dialog reopened) or the dialog is closed before this one resolves,
  // `isStale()` will report true and we discard whatever we get back.
  const requestId = ++activeStreamRequestId
  const isStale = () => requestId !== activeStreamRequestId || !props.modelValue

  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Camera access is not supported in this browser.')
    }

    const constraints: MediaStreamConstraints = {
      video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' },
      audio: false,
    }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)

    if (isStale()) {
      // The dialog was closed, or a newer camera request already started,
      // while this getUserMedia() call was still pending. Release the camera
      // immediately instead of adopting a stream nobody wants anymore.
      stream.getTracks().forEach((track) => track.stop())
      return
    }

    activeStream.value = stream

    if (videoRef.value) {
      videoRef.value.srcObject = stream
      await videoRef.value.play().catch(() => {
        // Autoplay can reject silently on some browsers; the visible play controls (if any) still work.
      })
    }

    if (isStale()) {
      // Became stale while awaiting video playback — release what we just assigned.
      stopActiveStream()
      return
    }

    // Device labels are only populated once permission has been granted, so
    // (re)enumerate after the first successful getUserMedia call.
    if (navigator.mediaDevices.enumerateDevices) {
      const allDevices = await navigator.mediaDevices.enumerateDevices()

      if (isStale()) {
        stopActiveStream()
        return
      }

      devices.value = allDevices.filter((device) => device.kind === 'videoinput')
    }

    const activeDeviceId = stream.getVideoTracks()[0]?.getSettings()?.deviceId
    selectedDeviceId.value = activeDeviceId || deviceId || devices.value[0]?.deviceId
  } catch (err) {
    if (!isStale()) {
      errorMessage.value = describeError(err)
    }
  } finally {
    if (!isStale()) {
      isStarting.value = false
    }
  }
}

const onDeviceChange = (deviceId: string) => {
  startStream(deviceId)
}

const retryStream = () => {
  startStream(selectedDeviceId.value)
}

const enterReviewMode = (imageUrl: string, width: number, height: number) => {
  if (reviewImageUrl.value) URL.revokeObjectURL(reviewImageUrl.value)
  reviewImageUrl.value = imageUrl
  reviewImageWidth.value = width
  reviewImageHeight.value = height
  cropRect.value = computeDefaultRect(width, height)
  mode.value = 'review'
}

const exitReviewMode = () => {
  if (reviewImageUrl.value) URL.revokeObjectURL(reviewImageUrl.value)
  reviewImageUrl.value = ''
  reviewImageWidth.value = 0
  reviewImageHeight.value = 0
  cropRect.value = { ...FULL_RECT }
  mode.value = 'live'
}

const capturePhoto = () => {
  const video = videoRef.value
  const canvas = canvasRef.value
  if (!video || !canvas || !video.videoWidth || !video.videoHeight) return

  canvas.width = video.videoWidth
  canvas.height = video.videoHeight
  const context = canvas.getContext('2d')
  if (!context) return
  context.drawImage(video, 0, 0, canvas.width, canvas.height)

  const operationId = ++photoOperationId

  canvas.toBlob(
    (blob) => {
      // Discard the result if Cancel/Retake was clicked (or a newer capture
      // started) while this conversion was still running in the background.
      if (!blob || operationId !== photoOperationId || !props.modelValue) return
      enterReviewMode(URL.createObjectURL(blob), canvas.width, canvas.height)
    },
    'image/jpeg',
    0.92,
  )
}

const retake = () => {
  invalidatePendingPhotoOperations()
  exitReviewMode()
}

const applyCropAndAdd = () => {
  const canvas = canvasRef.value
  if (!canvas || isAddingPhoto.value) return

  const rect = cropRect.value
  const sx = clamp(Math.round(rect.x * canvas.width), 0, canvas.width - 1)
  const sy = clamp(Math.round(rect.y * canvas.height), 0, canvas.height - 1)
  const sw = clamp(Math.round(rect.width * canvas.width), 1, canvas.width - sx)
  const sh = clamp(Math.round(rect.height * canvas.height), 1, canvas.height - sy)

  const cropCanvas = document.createElement('canvas')
  cropCanvas.width = sw
  cropCanvas.height = sh
  const context = cropCanvas.getContext('2d')
  if (!context) return
  context.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh)

  const operationId = ++photoOperationId
  isAddingPhoto.value = true

  cropCanvas.toBlob(
    (blob) => {
      // Discard the result if Cancel/Retake was clicked (or a newer capture/
      // crop started) while this conversion was still running in the
      // background — otherwise a canceled photo could silently reappear.
      if (operationId !== photoOperationId || !props.modelValue) return
      isAddingPhoto.value = false
      if (!blob) return

      const id = generateId()
      const file = new File([blob], `camera-photo-${Date.now()}.jpg`, { type: 'image/jpeg' })
      const previewUrl = URL.createObjectURL(blob)
      capturedShots.value.push({ id, file, previewUrl })
      exitReviewMode()
    },
    'image/jpeg',
    0.92,
  )
}

// --- Crop rectangle selection (pointer-driven move / resize / draw) --------

const getNormalizedPoint = (event: PointerEvent) => {
  const container = cropContainerRef.value
  if (!container) return { x: 0, y: 0 }
  const bounds = container.getBoundingClientRect()
  const x = bounds.width ? clamp((event.clientX - bounds.left) / bounds.width, 0, 1) : 0
  const y = bounds.height ? clamp((event.clientY - bounds.top) / bounds.height, 0, 1) : 0
  return { x, y }
}

const onPointerMove = (event: PointerEvent) => {
  if (!dragMode.value || !dragStartPointer.value || !dragStartRect.value) return
  const point = getNormalizedPoint(event)
  const dx = point.x - dragStartPointer.value.x
  const dy = point.y - dragStartPointer.value.y
  const start = dragStartRect.value

  if (dragMode.value === 'draw') {
    const x = Math.min(dragStartPointer.value.x, point.x)
    const y = Math.min(dragStartPointer.value.y, point.y)
    const width = Math.abs(point.x - dragStartPointer.value.x)
    const height = Math.abs(point.y - dragStartPointer.value.y)
    cropRect.value = { x, y, width, height }
    return
  }

  if (dragMode.value === 'move') {
    const maxX = Math.max(1 - start.width, 0)
    const maxY = Math.max(1 - start.height, 0)
    cropRect.value = {
      ...start,
      x: clamp(start.x + dx, 0, maxX),
      y: clamp(start.y + dy, 0, maxY),
    }
    return
  }

  // Resize modes: adjust the dragged corner while keeping the opposite corner anchored.
  const right = start.x + start.width
  const bottom = start.y + start.height
  let { x, y, width, height } = start

  if (dragMode.value === 'resize-nw') {
    x = clamp(start.x + dx, 0, right - MIN_RECT_SIZE)
    y = clamp(start.y + dy, 0, bottom - MIN_RECT_SIZE)
    width = right - x
    height = bottom - y
  } else if (dragMode.value === 'resize-ne') {
    const newRight = clamp(right + dx, x + MIN_RECT_SIZE, 1)
    y = clamp(start.y + dy, 0, bottom - MIN_RECT_SIZE)
    width = newRight - x
    height = bottom - y
  } else if (dragMode.value === 'resize-sw') {
    x = clamp(start.x + dx, 0, right - MIN_RECT_SIZE)
    const newBottom = clamp(bottom + dy, y + MIN_RECT_SIZE, 1)
    width = right - x
    height = newBottom - y
  } else if (dragMode.value === 'resize-se') {
    const newRight = clamp(right + dx, x + MIN_RECT_SIZE, 1)
    const newBottom = clamp(bottom + dy, y + MIN_RECT_SIZE, 1)
    width = newRight - x
    height = newBottom - y
  }

  cropRect.value = { x, y, width, height }
}

const onPointerUp = () => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)

  if (dragMode.value === 'draw' && (cropRect.value.width < MIN_RECT_SIZE || cropRect.value.height < MIN_RECT_SIZE)) {
    // Treat a plain click / accidental micro-drag as "no selection change".
    cropRect.value = computeDefaultRect(reviewImageWidth.value, reviewImageHeight.value)
  }

  dragMode.value = null
  dragStartPointer.value = null
  dragStartRect.value = null
}

const startDrag = (nextMode: Exclude<DragMode, null>, event: PointerEvent) => {
  event.preventDefault()
  const point = getNormalizedPoint(event)
  dragMode.value = nextMode
  dragStartPointer.value = point
  dragStartRect.value = nextMode === 'draw' ? { x: point.x, y: point.y, width: 0, height: 0 } : { ...cropRect.value }
  if (nextMode === 'draw') cropRect.value = dragStartRect.value as NormalizedRect

  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
}

const onBackgroundPointerDown = (event: PointerEvent) => {
  startDrag('draw', event)
}

const onRectPointerDown = (event: PointerEvent) => {
  startDrag('move', event)
}

const onHandlePointerDown = (corner: ResizeCorner, event: PointerEvent) => {
  startDrag(`resize-${corner}` as Exclude<DragMode, null>, event)
}

// --- Captured shots list ----------------------------------------------------

const removeShot = (id: string) => {
  const index = capturedShots.value.findIndex((shot) => shot.id === id)
  if (index >= 0) {
    URL.revokeObjectURL(capturedShots.value[index].previewUrl)
    capturedShots.value.splice(index, 1)
  }
}

const resetShots = () => {
  capturedShots.value.forEach((shot) => URL.revokeObjectURL(shot.previewUrl))
  capturedShots.value = []
}

const finish = () => {
  if (capturedShots.value.length === 0) return
  emit(
    'capture',
    capturedShots.value.map((shot) => shot.file),
  )
  resetShots()
  emit('update:modelValue', false)
}

const close = () => {
  invalidatePendingStreamRequests()
  invalidatePendingPhotoOperations()
  stopActiveStream()
  exitReviewMode()
  resetShots()
  emit('update:modelValue', false)
}

const onDialogUpdate = (value: boolean) => {
  if (!value) close()
  else emit('update:modelValue', value)
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      devices.value = []
      selectedDeviceId.value = undefined
      exitReviewMode()
      startStream()
    } else {
      invalidatePendingStreamRequests()
      invalidatePendingPhotoOperations()
      stopActiveStream()
    }
  },
)

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  invalidatePendingStreamRequests()
  invalidatePendingPhotoOperations()
  stopActiveStream()
  exitReviewMode()
  resetShots()
})
</script>

<style scoped>
.camera-preview-wrapper {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
}

.camera-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.camera-preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.crop-container {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  touch-action: none;
  cursor: crosshair;
  user-select: none;
}

.crop-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
  user-select: none;
}

.crop-rect {
  position: absolute;
  border: 2px solid #fff;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  cursor: move;
  touch-action: none;
}

.crop-handle {
  position: absolute;
  width: 16px;
  height: 16px;
  background: #fff;
  border: 2px solid rgb(var(--v-theme-primary));
  border-radius: 50%;
  touch-action: none;
}

.crop-handle-nw {
  top: -9px;
  left: -9px;
  cursor: nwse-resize;
}

.crop-handle-ne {
  top: -9px;
  right: -9px;
  cursor: nesw-resize;
}

.crop-handle-sw {
  bottom: -9px;
  left: -9px;
  cursor: nesw-resize;
}

.crop-handle-se {
  bottom: -9px;
  right: -9px;
  cursor: nwse-resize;
}

.captured-thumb {
  position: relative;
}

.captured-thumb-remove {
  position: absolute;
  top: -6px;
  right: -6px;
}
</style>
